import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import api from '../services/api';
import ApolloAuth from '../services/apolloAuth';
import Session from '../services/session';
import useStorageAuth from './useStorageAuth';

const AuthContext = createContext({});

// Sessão entregue pelo Due Studio 5 (aba "Projetos"): o app injeta esta variável
// antes dos scripts da página, com o token da Apolo — o mesmo que o portal usa.
// Ver PortalSession.cs / WebView2Host.cs no repositório do Due Studio 5.
const DUE_STUDIO_SESSION = '__DUE_STUDIO_SESSION__';

function getDueStudioSession() {
  const session = typeof window !== 'undefined' && window[DUE_STUDIO_SESSION];

  return session && session.token ? session : null;
}

const AuthProvider = ({ children }) => {
  const [storage, setStorageItem, , clearStorage, isLoading] = useStorageAuth();

  // Enquanto o handoff roda, isLoggedIn fica null (mesmo estado do carregamento do
  // storage) para as rotas não redirecionarem para a home de deslogado antes da hora.
  const [isHandoffPending, setIsHandoffPending] = useState(
    () => !!getDueStudioSession()
  );

  function formatName(fullName) {
    const splittedName = fullName.split(' ');
    if (splittedName[0] >= 6) {
      return splittedName[0];
    }

    return `${splittedName[0]} ${splittedName[1] || ''}`.trim();
  }

  const signIn = useCallback(
    async ({
      newToken,
      newRefreshToken,
      newUserImage,
      newUserType,
      newUserName,
    }) => {
      await Promise.all([
        setStorageItem('token', newToken),
        // O handoff do Due Studio 5 entrega só o access token; sem refresh, a
        // sessão dura o que o token durar, como antes.
        setStorageItem('refreshToken', newRefreshToken),
        setStorageItem('userImage', newUserImage),
        setStorageItem('userName', formatName(newUserName)),
        setStorageItem(
          'userType',
          newUserType === 'Admin' ? 'designer' : newUserType.toLowerCase()
        ),
      ]);
    },
    [setStorageItem]
  );

  const signOut = useCallback(async () => {
    // Lido do storage, e não do estado, para o `signOut` não trocar de
    // identidade a cada mudança de sessão.
    const refreshToken = await Session.getRefreshToken();

    await clearStorage();

    // Revogar na Apolo é o que encerra a sessão de verdade; se falhar, o storage
    // já foi limpo e o usuário está fora deste navegador.
    if (refreshToken) {
      await ApolloAuth.logout({ refreshToken }).catch(() => undefined);
    }
  }, [clearStorage]);

  useEffect(() => {
    if (isLoading || !isHandoffPending) return undefined;

    const session = getDueStudioSession();

    // Sem sessão do app, ou o storage já está com o mesmo token: nada a fazer.
    if (!session || session.token === storage.token) {
      setIsHandoffPending(false);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const { data: user } = await api.get('/user', {
          headers: { Authorization: `Bearer ${session.token}` },
          skipUnauthorizedRedirect: true,
        });

        if (cancelled) return;

        if (!user?.type || !user?.name) {
          throw new Error('Perfil incompleto retornado pela API');
        }

        await signIn({
          newToken: session.token,
          newUserImage: user.image?.url || '',
          newUserType: user.type,
          newUserName: user.name,
        });
      } catch (error) {
        // Token expirado/revogado no app: cai na tela de login normal do portal.
        console.error('Falha ao entrar com a sessão do Due Studio 5:', error);
      } finally {
        if (!cancelled) setIsHandoffPending(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoading, isHandoffPending, storage.token, signIn]);

  const updateUserImage = useCallback(
    async imageUrl => {
      await setStorageItem('userImage', imageUrl);
    },
    [setStorageItem]
  );

  return (
    <AuthContext.Provider
      value={{
        userImage: storage.userImage,
        isLoggedIn: isLoading || isHandoffPending ? null : !!storage.token,
        userType: storage.userType,
        userName: storage.userName,
        userToken: storage.token,
        signIn,
        signOut,
        updateUserImage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
